// Vendor Product Mapping Queue — Phase 5 of the MBO ↔ Vendor mapping bridge.
//
// One row per unmatched vendor_products entry. Admin picks an action:
//   Pick #1/2/3   → vendor_products.product_mapping_id = suggested master
//   Edit          → rename the vendor row + auto-retry the matcher
//   Create        → make a new product_mappings row from the vendor info
//   Skip          → bump last_skipped_at (row sinks to bottom, stays pending)

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { vendorApi, vendorMappingQueueApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import {
  Loader2, Search, Check, Edit2, Plus, SkipForward, X, ArrowRight, Info,
  Sparkles, ThumbsDown, Download, Upload,
} from 'lucide-react';

const PAGE_SIZE = 50;

export default function AdminVendorMapping() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, resolved: 0, pending_dispatched: 0, pending_catalog: 0 });
  const [tab, setTab] = useState('dispatched');

  // Denominator for the pager — depends on which tab is active.
  const tabTotal =
    tab === 'dispatched' ? (counts.pending_dispatched ?? 0) :
    tab === 'catalog'    ? (counts.pending_catalog ?? 0) :
                           (counts.pending ?? 0);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [aiFilter, setAiFilter] = useState('');
  const [page, setPage] = useState(0);
  const [openRowId, setOpenRowId] = useState(null);   // which row's action panel is open
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        vendorMappingQueueApi.list({
          vendorId: vendorFilter || undefined,
          tab,
          search: search || undefined,
          ai: aiFilter || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        }),
        vendorMappingQueueApi.counts({ vendorId: vendorFilter || undefined }),
      ]);
      setRows(list.data.rows || []);
      setCounts(c.data || { pending: 0, resolved: 0, pending_dispatched: 0, pending_catalog: 0 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load queue');
    } finally { setLoading(false); }
  }, [vendorFilter, search, page, tab, aiFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { vendorApi.list().then((r) => setVendors(r.data || [])).catch(() => {}); }, []);

  // Land on the tab that actually has work: if Dispatched is empty but
  // Catalog has pending items, switch once on first load.
  const autoTabbed = useRef(false);
  useEffect(() => {
    if (autoTabbed.current || loading) return;
    if (tab === 'dispatched' && counts.pending_dispatched === 0 && counts.pending_catalog > 0) {
      autoTabbed.current = true;
      setTab('catalog');
      setPage(0);
    } else if (counts.pending_dispatched > 0 || counts.pending_catalog > 0) {
      autoTabbed.current = true;
    }
  }, [loading, counts, tab]);

  async function downloadQuoteRequest() {
    try {
      const qs = vendorFilter ? `?vendorId=${vendorFilter}` : '';
      await downloadAuthed(`/admin/vendor-mapping/quote-request.csv${qs}`, 'quote-request.csv');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    }
  }

  const quoteFileRef = useRef(null);
  const [uploadingQuotes, setUploadingQuotes] = useState(false);
  async function uploadFilledQuotes(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!vendorFilter) {
      toast.error('Select the vendor in the Vendor filter first, then upload the filled file.');
      return;
    }
    setUploadingQuotes(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await vendorApi.bulkUploadPricing(vendorFilter, fd);
      const d = res.data;
      const applied = (d.pricingRowsCreated || 0) + (d.pricingUnchanged || 0);
      const errList = (d.errors || []).slice(0, 4)
        .map((er) => `row ${er.row}: ${er.error}`).join('\n');

      if (applied === 0) {
        // Total failure must look like one — not a green "imported" toast.
        toast.error(
          `Nothing was imported — ${d.errors?.length || 0} of ${d.rowsParsed} row(s) failed.\n` +
          errList + ((d.errors?.length || 0) > 4 ? `\n…and ${d.errors.length - 4} more` : ''),
          { duration: 15000, style: { whiteSpace: 'pre-line' } }
        );
        return;
      }

      toast.success(
        `Prices imported — ${d.pricingRowsCreated} price(s) added` +
        (d.pricingUnchanged ? `, ${d.pricingUnchanged} already current` : '') +
        (d.mappedAuto ? `, ${d.mappedAuto} auto-linked` : ''),
        { duration: 8000 }
      );
      if (d.errors?.length) {
        toast(
          `${d.errors.length} row(s) skipped:\n` + errList +
          (d.errors.length > 4 ? `\n…and ${d.errors.length - 4} more` : ''),
          { duration: 12000, icon: '⚠️', style: { whiteSpace: 'pre-line' } }
        );
      }
      if (d.warnings?.length) {
        toast(`${d.warnings.length} warning(s):\n${d.warnings.slice(0, 3).map((w) => `row ${w.row} — ${w.warning}`).join('\n')}`,
          { duration: 10000, icon: '⚠️', style: { whiteSpace: 'pre-line' } });
      }
      if (d.stillUnpriced) {
        if (d.stillUnpriced.lineCount > 0) {
          toast(
            `Still unpriced (dispatches since ${d.stillUnpriced.from}): ${d.stillUnpriced.lineCount} line(s) across ` +
            `${d.stillUnpriced.productCount} product(s) — blank rows, AI matches awaiting approval, or effective dates.`,
            { duration: 10000, icon: '⚠️' }
          );
        } else {
          toast.success('All dispatched lines for this vendor are now priced.', { duration: 8000 });
        }
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploadingQuotes(false); }
  }

  async function pickSuggestion(rowId, masterId) {
    try {
      await vendorMappingQueueApi.link(rowId, masterId);
      toast.success('Linked');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Link failed'); }
  }

  async function skipRow(rowId) {
    try {
      await vendorMappingQueueApi.skip(rowId);
      toast.success('Skipped — will reappear later');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Skip failed'); }
  }

  async function aiApprove(rowId) {
    try {
      await vendorMappingQueueApi.aiApprove(rowId);
      toast.success('AI suggestion approved — linked');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Approve failed'); }
  }

  async function aiReject(rowId) {
    try {
      await vendorMappingQueueApi.aiReject(rowId);
      toast.success('AI suggestion dismissed');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Reject failed'); }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Vendor product mapping</h1>
        <button onClick={downloadQuoteRequest}
                title="CSV of dispatched products with no vendor price and no AI match — email it to the vendor to get quotes"
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-white text-rose-700 border-rose-300 hover:bg-rose-50">
          <Download className="w-3.5 h-3.5" /> Quote request (CSV)
        </button>
        <button onClick={() => quoteFileRef.current?.click()}
                disabled={uploadingQuotes}
                title="Upload the same file once the vendor fills the unit_price column — prices and product links land automatically (select the vendor in the filter first)"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-50">
          {uploadingQuotes ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Upload filled quotes
        </button>
        <input ref={quoteFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
               onChange={uploadFilledQuotes} />
        <button onClick={() => setShowHelp((v) => !v)}
                title="What do these buttons do?"
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${showHelp ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}>
          <Info className="w-3.5 h-3.5" /> {showHelp ? 'Hide help' : 'How this page works'}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-3">
        Vendor products that didn't auto-match to the Product Mapping master.
        Pick a suggested master, rename the vendor's product, create a new
        master entry, or skip for later.
      </p>

      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}

      <UnpricedBanner refreshKey={counts} onDownload={downloadQuoteRequest} />

      <AiStatusBar onRunFinished={load} />

      {/* Tabs — Dispatched (priority) and Catalog only */}
      <div className="flex items-center gap-2 mb-3 border-b border-gray-200">
        <TabButton active={tab === 'dispatched'}
                   onClick={() => { setTab('dispatched'); setPage(0); }}>
          Dispatched <span className="ml-1 text-xs">({counts.pending_dispatched ?? 0})</span>
        </TabButton>
        <TabButton active={tab === 'catalog'}
                   onClick={() => { setTab('catalog'); setPage(0); }}>
          Catalog only <span className="ml-1 text-xs">({counts.pending_catalog ?? 0})</span>
        </TabButton>
        <TabButton active={tab === 'all'}
                   onClick={() => { setTab('all'); setPage(0); }}>
          All <span className="ml-1 text-xs">({counts.pending ?? 0})</span>
        </TabButton>
        <span className="ml-auto pb-2 text-xs text-gray-500">
          <strong className="text-gray-800">Dispatched</strong> = we've shipped this product to the vendor.
          {' '}<strong>Catalog only</strong> = in vendor's catalog but we've never ordered it.
        </span>
      </div>

      {(tab === 'dispatched' || tab === 'catalog') && (
        <BulkAiApproveBar scope={tab} onDone={() => { load(); }} />
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase text-gray-500">Vendor</span>
        <select value={vendorFilter} onChange={(e) => { setVendorFilter(e.target.value); setPage(0); }}
                className="px-2 py-1 border border-gray-300 rounded text-sm">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-gray-400" />
          <input value={searchInput}
                 onChange={(e) => setSearchInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(0); } }}
                 placeholder="Search vendor product name…"
                 className="pl-7 pr-2 py-1 border border-gray-300 rounded text-sm w-72" />
        </div>
        <button onClick={() => { setSearch(searchInput); setPage(0); }}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
          Apply
        </button>
        <span className="text-xs uppercase text-gray-500 ml-2">AI</span>
        <select value={aiFilter} onChange={(e) => { setAiFilter(e.target.value); setPage(0); }}
                className="px-2 py-1 border border-gray-300 rounded text-sm">
          <option value="">All rows</option>
          <option value="match">AI found a match</option>
          <option value="unsure">AI unsure</option>
          <option value="no_match">Needs vendor quote</option>
          <option value="rejected">AI rejected by admin</option>
          <option value="none">AI not run yet</option>
        </select>
        <span className="ml-auto text-xs text-gray-500">
          <strong className="text-gray-800">{counts.pending}</strong> pending
          {counts.resolved > 0 && <> · {counts.resolved} resolved</>}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
          {counts.pending > 0 ? (
            <>
              <p>Nothing pending in this tab{aiFilter || search || vendorFilter ? ' with the current filters' : ''}.</p>
              {tab === 'dispatched' && counts.pending_catalog > 0 && (
                <button onClick={() => { setTab('catalog'); setPage(0); }}
                        className="mt-2 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm hover:bg-blue-50">
                  {counts.pending_catalog} item(s) in Catalog only →
                </button>
              )}
              {tab === 'catalog' && counts.pending_dispatched > 0 && (
                <button onClick={() => { setTab('dispatched'); setPage(0); }}
                        className="mt-2 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm hover:bg-blue-50">
                  {counts.pending_dispatched} item(s) in Dispatched →
                </button>
              )}
            </>
          ) : (
            'No pending vendor mappings. Upload a vendor catalog to populate the queue.'
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <QueueRow key={r.id} row={r}
                      isOpen={openRowId === r.id}
                      onOpen={(open) => setOpenRowId(open ? r.id : null)}
                      onPick={(masterId) => pickSuggestion(r.id, masterId)}
                      onSkip={() => skipRow(r.id)}
                      onAiApprove={() => aiApprove(r.id)}
                      onAiReject={() => aiReject(r.id)}
                      onChanged={load} />
          ))}

          {/* Pager */}
          <div className="flex items-center justify-center gap-2 pt-3 text-sm">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50">
              ← Prev
            </button>
            <span className="text-gray-600 text-xs">
              Page {page + 1} of {Math.max(1, Math.ceil(tabTotal / PAGE_SIZE))}
            </span>
            <button onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * PAGE_SIZE >= tabTotal}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50">
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// One-click clearance of the high-confidence AI matches on the Dispatched
// tab (Venky 2026-06-11): ~547 of the 570 dispatched pending matches are
// ≥90% confidence — clicking each one is unworkable. Two-step confirm, no
// browser dialog.
// Payables-gap worklist — same live data as the Owner Cockpit alert, shown
// here because this is where the dispatch team can actually act on it:
// approve the AI matches below, or download the quote request for the rest.
function UnpricedBanner({ refreshKey, onDownload }) {
  const [up, setUp] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    vendorMappingQueueApi.unpriced().then((r) => setUp(r.data)).catch(() => {});
  }, [refreshKey]);

  if (!up || up.productCount === 0) return null;
  return (
    <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-rose-900">
          {up.lineCount} dispatched line(s) across {up.productCount} product(s) are billing ₹0 in Vendor Payables this month
        </span>
        {up.fixableByApprove > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium">
            {up.fixableByApprove} fixable — approve the AI match below
          </span>
        )}
        {up.needsQuote > 0 && (
          <button onClick={onDownload}
                  className="text-xs px-2 py-0.5 rounded-full bg-rose-600 text-white font-medium hover:bg-rose-700"
                  title="Download the quote-request CSV and email it to the vendor">
            {up.needsQuote} need a vendor quote — download list
          </button>
        )}
        <button onClick={() => setExpanded((v) => !v)}
                className="ml-auto text-xs text-rose-700 hover:underline">
          {expanded ? 'hide products' : 'show products'}
        </button>
      </div>
      {expanded && (
        <ul className="mt-2 space-y-1 text-xs text-gray-800">
          {up.items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{it.indian_brand || it.product_name}</span>
              {it.indian_brand && it.product_name !== it.indian_brand && (
                <span className="text-gray-500">"{it.product_name}"</span>
              )}
              <span className="text-gray-500">({it.vendor_name})</span>
              <span className="text-gray-500">{it.line_count} line(s) · qty {it.total_qty}</span>
              {it.has_ai_match ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">AI match ready</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">needs quote</span>
              )}
            </li>
          ))}
          {up.productCount > up.items.length && (
            <li className="text-gray-500">…and {up.productCount - up.items.length} more product(s) — all in the quote-request CSV.</li>
          )}
        </ul>
      )}
      <p className="mt-1 text-xs text-rose-800">
        When the vendor sends prices back: Vendors page → vendor → bulk pricing import (or add a price per product).
      </p>
    </div>
  );
}

function BulkAiApproveBar({ scope = 'dispatched', onDone }) {
  const [arming, setArming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    setRunning(true);
    setArming(false);
    try {
      const body = scope === 'catalog'
        ? { dispatchedOnly: false, catalogOnly: true, minConfidence: 0.9 }
        : { dispatchedOnly: true, minConfidence: 0.9 };
      const res = await vendorMappingQueueApi.aiBulkApprove(body);
      setResult(res.data);
      toast.success(`Approved ${res.data.approved} of ${res.data.candidates} AI matches`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk approve failed');
    } finally { setRunning(false); }
  }

  return (
    <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm flex flex-wrap items-center gap-3">
      <Sparkles className="w-4 h-4 text-indigo-600 shrink-0" />
      <div className="flex-1 min-w-[16rem] text-indigo-900">
        <strong>Clear the backlog in one go:</strong> approve every AI match the model is
        ≥90% sure about ({scope === 'catalog' ? 'catalog-only products' : 'dispatched products only'}).
        Each approval is audit-logged under your name; anything below 90% stays here for your eyes.
      </div>
      {result && (
        <span className="text-xs text-indigo-800">
          last run: {result.approved}/{result.candidates} approved{result.failed > 0 && `, ${result.failed} failed`}
        </span>
      )}
      {!arming ? (
        <button onClick={() => setArming(true)} disabled={running}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Approve all ≥90% matches…'}
        </button>
      ) : (
        <span className="flex items-center gap-2">
          <button onClick={run}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700">
            Yes, approve them
          </button>
          <button onClick={() => setArming(false)}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-white">
            Cancel
          </button>
        </span>
      )}
    </div>
  );
}

function HelpPanel({ onClose }) {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs text-gray-800">
      <div className="flex items-start justify-between mb-2">
        <div className="text-sm font-semibold text-blue-900">How vendor product mapping works</div>
        <button onClick={onClose} className="text-blue-700 hover:underline text-xs">close</button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded bg-white border border-blue-100 p-3">
          <div className="font-medium text-gray-900 mb-1">What is this page for?</div>
          <p>
            When you upload a vendor's price list, our system tries to auto-match each vendor product to a Product Mapping master row (so Vendor Payables can compute what you owe per dispatched order). Products that didn't auto-match land here for you to resolve.
          </p>
        </div>

        <div className="rounded bg-white border border-blue-100 p-3">
          <div className="font-medium text-gray-900 mb-1">The two tabs</div>
          <ul className="list-disc pl-4 space-y-0.5">
            <li><strong>Dispatched</strong> — products you've actually shipped through this vendor. Highest priority — these directly affect Vendor Payables totals.</li>
            <li><strong>Catalog only</strong> — products in the vendor's catalog you've never ordered. Lower priority; can be done later as orders come in.</li>
            <li><strong>All</strong> — both combined.</li>
          </ul>
          <p className="mt-1 text-[11px] text-gray-600">As new orders flow through, products auto-move from "Catalog only" to "Dispatched" — you don't need to manage this manually.</p>
        </div>

        <div className="rounded bg-white border border-blue-100 p-3">
          <div className="font-medium text-gray-900 mb-1">Actions on each row</div>
          <ul className="list-none space-y-1.5">
            <li>
              <span className="inline-block bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded font-mono text-[10px]">Pick #1 / #2 / #3</span>
              {' '}— links the vendor product to that master entry. The score (e.g. "0.92") shows how confident the system is. Highest = best guess.
              <div className="ml-1 text-[11px] text-gray-600 mt-0.5">After picking: the row leaves the queue; Vendor Payables can now price any orders for this product.</div>
            </li>
            <li>
              <span className="inline-block border border-gray-300 px-1.5 py-0.5 rounded font-mono text-[10px]">Edit / Create</span>
              {' '}— opens a panel with two sub-actions:
              <ul className="list-disc pl-5 mt-0.5 space-y-0.5">
                <li><strong>Create new master</strong> — none of the suggestions are right. Creates a brand-new master entry (Indian brand + generic + container), then links this vendor product to it. The container is auto-parsed from FRT's "Packing" (e.g. "1X20GM" → "Tube 20g") if you don't enter one.</li>
                <li><strong>Rename vendor product</strong> — fixes typos in the vendor's name (e.g. "Tadalis 20mg TAB" → "Tadalis 20mg") and re-runs the matcher. If it now matches a master, the row auto-resolves.</li>
              </ul>
            </li>
            <li>
              <span className="inline-block border border-gray-300 px-1.5 py-0.5 rounded font-mono text-[10px]">Skip</span>
              {' '}— "I'll deal with this later". Row stays in the queue but moves to the bottom (sorted by skip time). It will come back into view next time you scroll past your current page. Re-uploading does not reset the skip — the row stays skipped until you explicitly resolve it.
            </li>
          </ul>
        </div>

        <div className="rounded bg-white border border-blue-100 p-3">
          <div className="font-medium text-gray-900 mb-1">After you finish</div>
          <p>
            Once a vendor product is linked to a master, the system can match it to MBO orders that dispatch the same master product. From there:
          </p>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            <li>Run <strong>Vendor Payables</strong> (Super admin → Vendor payables) — that vendor's invoice line for the period shows up with INR pricing.</li>
            <li>If the same product shows up in a new MBO upload, it auto-prices on dispatch.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              active
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}>
      {children}
    </button>
  );
}

function QueueRow({ row, isOpen, onOpen, onPick, onSkip, onAiApprove, onAiReject, onChanged }) {
  return (
    <div className={`bg-white border rounded-xl p-3 ${row.last_skipped_at ? 'opacity-60 border-gray-200' : 'border-gray-200'}`}>
      {row.ai_suggestion_kind && row.ai_suggestion_kind !== 'rejected' && (
        <AiSuggestion row={row} onApprove={onAiApprove} onReject={onAiReject} />
      )}
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] uppercase font-semibold text-gray-400 tracking-wide">{row.vendor_name}</span>
            {row.last_skipped_at && (
              <span className="text-[10px] text-gray-400">skipped</span>
            )}
            {row.unit_price != null && (
              <span className="text-[11px] text-gray-500 ml-auto">
                ₹{Number(row.unit_price).toFixed(2)}
              </span>
            )}
          </div>
          <div className="font-medium text-gray-900 truncate">{row.raw_name}</div>
          <div className="text-xs text-gray-500 mt-0.5 space-x-2">
            {row.raw_brand && <span>Brand: {row.raw_brand}</span>}
            {row.raw_manufacturer && <span>Mfr: {row.raw_manufacturer}</span>}
            {row.raw_generic && <span>Gen: {row.raw_generic}</span>}
            {row.raw_packing && <span>Packing: {row.raw_packing}</span>}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {row.suggestions.length === 0 ? (
            <div className="text-xs text-gray-500 italic">No master suggestions above 0.40 similarity</div>
          ) : (
            <div className="space-y-1.5">
              {row.suggestions.map((s, i) => (
                <button key={s.masterId} onClick={() => onPick(s.masterId)}
                        title={`Link this vendor product to "${s.indianBrand}" in the master. Score ${s.score} (0 = no overlap, 1 = identical).`}
                        className="block w-full text-left text-xs px-2 py-1.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-emerald-900">{i + 1}.</span>
                    <span className="text-emerald-900">{s.indianBrand}</span>
                    <span className="text-emerald-700 text-[11px]">({s.score})</span>
                    <ArrowRight className="w-3 h-3 ml-auto text-emerald-700" />
                  </div>
                  <div className="text-[11px] text-gray-600 truncate">
                    {s.genericName}{s.container ? ` · ${s.container}` : ''}{s.manufacturer ? ` · ${s.manufacturer}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 w-32">
          <button onClick={() => onOpen(!isOpen)}
                  title="Open a panel to: (a) create a new master entry from this vendor's data, or (b) rename the vendor product and re-run the matcher."
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
            <Edit2 className="w-3 h-3" /> Edit / Create
          </button>
          <button onClick={onSkip}
                  title="Snooze this row — it stays in the queue but moves to the bottom. Re-uploads do not reset the skip."
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
            <SkipForward className="w-3 h-3" /> Skip
          </button>
        </div>
      </div>

      {isOpen && (
        <EditCreatePanel row={row} onClose={() => onOpen(false)} onChanged={onChanged} />
      )}
    </div>
  );
}

function EditCreatePanel({ row, onClose, onChanged }) {
  const [mode, setMode] = useState('create');   // 'edit' | 'create'
  // Edit: rename vendor product
  const [newName, setNewName] = useState(row.raw_name);
  // Create: pre-fill from vendor row + parsed container
  const initial = useMemo(() => ({
    indianBrand: row.raw_brand || row.raw_name,
    genericName: row.raw_generic || '',
    manufacturer: row.raw_manufacturer || '',
    container: '', // server will parse from raw_packing if blank
  }), [row]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  async function saveEdit() {
    if (!newName.trim()) { toast.error('New name required'); return; }
    setSaving(true);
    try {
      const r = await vendorMappingQueueApi.editVendorProductName(row.id, newName.trim());
      toast.success(r.data.resolved ? 'Renamed + auto-mapped' : 'Renamed (still in queue)');
      onClose();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Edit failed');
    } finally { setSaving(false); }
  }

  async function saveCreate() {
    if (!draft.indianBrand.trim() || !draft.genericName.trim()) {
      toast.error('Indian brand and generic name are required'); return;
    }
    setSaving(true);
    try {
      const r = await vendorMappingQueueApi.createMaster(row.id, {
        indianBrand: draft.indianBrand.trim(),
        genericName: draft.genericName.trim(),
        manufacturer: draft.manufacturer.trim() || null,
        container: draft.container.trim() || null,
      });
      toast.success(`New master created${r.data.container ? ` (container: ${r.data.container})` : ''}`);
      onClose();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="flex gap-2 mb-2 text-xs">
        <button onClick={() => setMode('create')}
                className={`px-3 py-1 rounded ${mode === 'create' ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>
          <Plus className="w-3 h-3 inline mr-1" /> Create new master
        </button>
        <button onClick={() => setMode('edit')}
                className={`px-3 py-1 rounded ${mode === 'edit' ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>
          <Edit2 className="w-3 h-3 inline mr-1" /> Rename vendor product
        </button>
        <button onClick={onClose}
                className="ml-auto px-2 py-1 text-gray-500 hover:bg-gray-100 rounded">
          <X className="w-3 h-3" />
        </button>
      </div>

      {mode === 'create' ? (
        <>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label>
              <span className="block text-gray-500 mb-0.5">Indian brand *</span>
              <input value={draft.indianBrand}
                     onChange={(e) => setDraft({ ...draft, indianBrand: e.target.value })}
                     className="w-full px-2 py-1 border border-gray-300 rounded" />
            </label>
            <label>
              <span className="block text-gray-500 mb-0.5">Generic name *</span>
              <input value={draft.genericName}
                     onChange={(e) => setDraft({ ...draft, genericName: e.target.value })}
                     className="w-full px-2 py-1 border border-gray-300 rounded" />
            </label>
            <label>
              <span className="block text-gray-500 mb-0.5">Manufacturer</span>
              <input value={draft.manufacturer}
                     onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })}
                     className="w-full px-2 py-1 border border-gray-300 rounded" />
            </label>
            <label>
              <span className="block text-gray-500 mb-0.5">
                Container {row.raw_packing && <span className="text-gray-400">(blank → parsed from "{row.raw_packing}")</span>}
              </span>
              <input value={draft.container}
                     onChange={(e) => setDraft({ ...draft, container: e.target.value })}
                     className="w-full px-2 py-1 border border-gray-300 rounded" />
            </label>
          </div>
          <div className="mt-2 flex justify-end">
            <button onClick={saveCreate} disabled={saving}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Create + link
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="block text-xs">
            <span className="block text-gray-500 mb-0.5">Vendor product name</span>
            <input value={newName} onChange={(e) => setNewName(e.target.value)}
                   className="w-full px-2 py-1 border border-gray-300 rounded" />
          </label>
          <div className="text-[11px] text-gray-500 mt-1">
            After save, the matcher re-runs on the new name. If it now matches a master, the row auto-resolves.
          </div>
          <div className="mt-2 flex justify-end">
            <button onClick={saveEdit} disabled={saving}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save rename
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// --- AI matcher (docs/ai-product-matcher-plan.md E3) ---------------------

function AiSuggestion({ row, onApprove, onReject }) {
  const kind = row.ai_suggestion_kind;
  const pct = row.ai_confidence != null ? Math.round(Number(row.ai_confidence) * 100) : null;
  const confident = Number(row.ai_confidence) >= 0.7;

  const box =
    kind === 'match' ? (confident ? 'border-indigo-200 bg-indigo-50' : 'border-amber-200 bg-amber-50')
    : kind === 'unsure' ? 'border-amber-200 bg-amber-50'
    : 'border-rose-200 bg-rose-50';

  return (
    <div className={`mb-2 rounded-lg border px-3 py-2 text-xs ${box}`}>
      <div className="flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 mt-0.5 text-indigo-600 shrink-0" />
        <div className="flex-1 min-w-0">
          {kind === 'match' && (
            <>
              <span className="font-semibold text-gray-900">
                AI suggests: {row.ai_master_brand}
              </span>
              <span className="text-gray-600">
                {row.ai_master_generic ? ` · ${row.ai_master_generic}` : ''}
                {row.ai_master_container ? ` · ${row.ai_master_container}` : ''}
                {row.ai_master_manufacturer ? ` · ${row.ai_master_manufacturer}` : ''}
              </span>
              {pct != null && (
                <span className={`ml-2 px-1.5 py-0.5 rounded font-mono text-[10px] ${confident ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
                  {pct}%{!confident && ' — check carefully'}
                </span>
              )}
            </>
          )}
          {kind === 'unsure' && (
            <span className="font-semibold text-amber-900">AI is unsure — your judgment needed</span>
          )}
          {kind === 'no_match' && (
            <span className="font-semibold text-rose-900">
              AI: vendor doesn't appear to carry this — needs a price quote from the vendor
            </span>
          )}
          {row.ai_reasoning && (
            <div className="text-gray-600 mt-0.5">{row.ai_reasoning}</div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {kind === 'match' && (
            <button onClick={onApprove}
                    title="Link this vendor product to the AI-suggested master. Audit-logged under your name."
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700">
              <Check className="w-3 h-3" /> Approve
            </button>
          )}
          <button onClick={onReject}
                  title="Dismiss the AI suggestion. The row stays in the queue for manual handling."
                  className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700">
            <ThumbsDown className="w-3 h-3" /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function AiStatusBar({ onRunFinished }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const wasRunning = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const r = await vendorMappingQueueApi.aiStatus();
      setStatus(r.data);
      if (wasRunning.current && !r.data.run?.running) {
        wasRunning.current = false;
        toast.success('AI run finished');
        onRunFinished?.();
      }
      if (r.data.run?.running) wasRunning.current = true;
    } catch { /* status strip is best-effort */ }
  }, [onRunFinished]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!status?.run?.running) return undefined;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [status?.run?.running, refresh]);

  async function startRun() {
    setStarting(true);
    try {
      const r = await vendorMappingQueueApi.aiRunBacklog({});
      toast.success(`AI run started on ${r.data.rows} rows`);
      wasRunning.current = true;
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start AI run');
    } finally { setStarting(false); }
  }

  if (!status) return null;
  if (!status.enabled) {
    return (
      <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        <Sparkles className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
        AI matcher is off (no OPENROUTER_API_KEY on the server). The queue works as usual.
      </div>
    );
  }

  const { run, budget, queue } = status;
  return (
    <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      <span className="flex items-center gap-1 font-semibold text-indigo-900">
        <Sparkles className="w-3.5 h-3.5" /> AI matcher
      </span>
      {run.running ? (
        <span className="flex items-center gap-1 text-indigo-800">
          <Loader2 className="w-3 h-3 animate-spin" />
          Running… {run.processed}/{run.total}
          {' '}(✓{run.matched} match · ?{run.unsure} · ✗{run.noMatch} no-match)
        </span>
      ) : (
        <>
          <span className="text-gray-700">
            <strong>{queue.not_run}</strong> pending rows not analysed yet
          </span>
          <span className="text-gray-700">
            suggested: <strong className="text-indigo-800">{queue.suggested_match} matches</strong>
            {' · '}{queue.suggested_unsure} unsure
            {' · '}<span className="text-rose-700">{queue.suggested_no_match} need quote</span>
          </span>
        </>
      )}
      <span className="text-gray-500">
        budget ${budget.usdUsed.toFixed(2)}/${budget.usdMax} · {budget.callsUsed}/{budget.callsMax} calls
      </span>
      {run.stopReason && !run.running && (
        <span className="text-rose-700">stopped: {run.stopReason}</span>
      )}
      {!run.running && queue.not_run > 0 && (
        <button onClick={startRun} disabled={starting}
                title="Run the AI over every pending row that hasn't been analysed. Runs in the background; results appear on rows as they finish."
                className="ml-auto flex items-center gap-1 px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
          {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Run AI on backlog
        </button>
      )}
    </div>
  );
}
